import React, { useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import { employeeSchema, employeeDefaults } from "../../../../models/employee";
import PropTypes from "prop-types";
import { FormPhotoUpload, NeoCard, NeoInput, NeoButton, NeoBadge, OverlayLoader } from "../../../../components/Common";
import { GENDER_OPTIONS } from "../../../../constants/gender";
import { GLOBAL_STATUS } from "../../../../constants/common";

const convertISOToBrDate = (isoDate) => {
    if (!isoDate) return "";
    const parts = isoDate.split("-");
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return isoDate;
};

const convertBrDateToISO = (brDate) => {
    if (!brDate) return "";
    const parts = brDate.split("/");
    if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        if (day.length === 2 && month.length === 2 && year.length === 4) {
            return `${year}-${month}-${day}`;
        }
    }
    return "invalid";
};

/**
 * EmployeeEditor
 * Formulário principal de Colaboradores.
 * Migrado para o Neo Design System.
 */
const EmployeeEditor = ({ employee, roles, onSave, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: employeeDefaults,
        validationSchema: employeeSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    const [birthDateInput, setBirthDateInput] = React.useState("");

    // Sincroniza estado
    useEffect(() => {
        if (employee) {
            formik.resetForm({
                values: {
                    ...employeeDefaults,
                    ...employee,
                    address: {
                        ...employeeDefaults.address,
                        ...(employee.address || {})
                    }
                }
            });
            if (employee.birthDate) {
                setBirthDateInput(convertISOToBrDate(employee.birthDate));
            } else {
                setBirthDateInput("");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee]);

    const handleBirthDateChange = (e) => {
        let val = e.target.value;
        val = val.replace(/\D/g, "");
        if (val.length > 8) val = val.substring(0, 8);
        
        let formatted = val;
        if (val.length > 4) {
            formatted = `${val.substring(0, 2)}/${val.substring(2, 4)}/${val.substring(4)}`;
        } else if (val.length > 2) {
            formatted = `${val.substring(0, 2)}/${val.substring(2)}`;
        }
        
        setBirthDateInput(formatted);
        onChange(true);
        
        if (formatted.length === 10) {
            formik.setFieldValue("birthDate", convertBrDateToISO(formatted));
        } else if (formatted.length === 0) {
            formik.setFieldValue("birthDate", "");
        } else {
            formik.setFieldValue("birthDate", "invalid");
        }
    };

    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!employee) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-card-account-details-outline"></i>
                        </div>
                    </div>
                    <h5>Selecione um colaborador</h5>
                    <p className="text-muted">Escolha um colaborador na lista ao lado ou cadastre um novo para dar acessos ao sistema.</p>
                </div>
            </div>
        );
    }

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{formik.values.id ? formik.values.fullName : "Novo Colaborador"}</span>
                    {formik.values.id && (
                        <NeoBadge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                            {formik.values.status === GLOBAL_STATUS.ACTIVE ? 'Ativo' : 'Inativo'}
                        </NeoBadge>
                    )}
                </span>
            }
            subtitle="Dados completos e Acessos"
            headerIcon="mdi-account-tie"
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    <NeoButton
                        variant="primary"
                        loading={isSaving}
                        loadingText="Salvando..."
                        onClick={formik.handleSubmit}
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative animate__animated animate__fadeIn"
            bodyClassName="p-4"
            noBorder={true}
        >
            <OverlayLoader show={isSaving} label="Salvando colaborador..." />
            
            <Row className="mb-4">
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-account-circle-outline me-2 font-size-16 text-primary"></i>
                        Perfil e Credenciais
                    </h6>
                </Col>

                <Col md="3" className="text-center mb-4 mb-md-0 d-flex flex-column align-items-center justify-content-center">
                    <FormPhotoUpload
                        photoPreview={formik.values.photoUrl}
                        onChange={(url) => {
                            formik.setFieldValue("photoUrl", url);
                            onChange(true);
                        }}
                        size="lg"
                        roundedCircle={false}
                    />
                </Col>

                <Col md="9">
                    <Row>
                        <Col md="4" className="mb-3">
                            <NeoInput
                                label="Nome"
                                name="firstName"
                                type="text"
                                flat={true}
                                placeholder="Ex: João"
                                error={formik.touched.firstName && formik.errors.firstName ? formik.errors.firstName : ""}
                                {...formik.getFieldProps("firstName")}
                            />
                        </Col>

                        <Col md="4" className="mb-3">
                            <NeoInput
                                label="Sobrenome"
                                name="lastName"
                                type="text"
                                flat={true}
                                placeholder="Ex: da Silva"
                                error={formik.touched.lastName && formik.errors.lastName ? formik.errors.lastName : ""}
                                {...formik.getFieldProps("lastName")}
                            />
                        </Col>

                        <Col md="4" className="mb-3">
                            <div className="neo-input-wrapper neo-input-wrapper-md">
                                <label className="neo-input-label">Status</label>
                                <div className="form-check form-switch pt-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                                        onChange={(e) => {
                                            formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                            onChange(true);
                                        }}
                                        className="form-check-input custom-switch-primary cursor-pointer"
                                        id="employee-status-switch"
                                    />
                                    <label className="form-check-label ms-2 small text-muted cursor-pointer" htmlFor="employee-status-switch">
                                        {formik.values.status === GLOBAL_STATUS.ACTIVE ? 'Acesso Liberado' : 'Acesso Bloqueado'}
                                    </label>
                                </div>
                            </div>
                        </Col>

                        <Col md={formik.values.id ? "6" : "4"} className="mb-3">
                            <NeoInput
                                label="Email de Acesso"
                                name="email"
                                type="email"
                                flat={true}
                                placeholder="exemplo@gmail.com"
                                error={formik.touched.email && formik.errors.email ? formik.errors.email : ""}
                                {...formik.getFieldProps("email")}
                                disabled={!!formik.values.id}
                                title={!!formik.values.id ? "O e-mail de acesso não pode ser alterado por aqui" : ""}
                            />
                        </Col>

                        {!formik.values.id && (
                            <Col md="4" className="mb-3">
                                <NeoInput
                                    label="Senha de Acesso"
                                    name="password"
                                    type="text"
                                    flat={true}
                                    placeholder="Crie uma senha forte"
                                    error={formik.touched.password && formik.errors.password ? formik.errors.password : ""}
                                    {...formik.getFieldProps("password")}
                                />
                            </Col>
                        )}

                        <Col md={formik.values.id ? "6" : "4"} className="mb-3">
                            <NeoInput
                                label="Cargo / Permissões"
                                name="role"
                                type="select"
                                flat={true}
                                error={formik.touched.role && formik.errors.role ? formik.errors.role : ""}
                                {...formik.getFieldProps("role")}
                            >
                                <option value="">Selecione um Cargo</option>
                                <option value="admin">Administrador (Root)</option>
                                {roles?.map(role => (
                                    <option key={role.id} value={role.id}>{role.name}</option>
                                ))}
                            </NeoInput>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <hr className="my-4 border-dashed" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }} />

            <Row className="mb-4">
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-clipboard-account-outline me-2 font-size-16 text-primary"></i>
                        Dados Pessoais
                    </h6>
                </Col>

                <Col md="3" className="mb-3">
                    <NeoInput
                        label="CPF"
                        name="cpf"
                        type="text"
                        flat={true}
                        placeholder="000.000.000-00"
                        {...formik.getFieldProps("cpf")}
                    />
                </Col>

                <Col md="3" className="mb-3">
                    <NeoInput
                        label="Telefone / WhatsApp"
                        name="phone"
                        type="text"
                        flat={true}
                        placeholder="(11) 90000-0000"
                        {...formik.getFieldProps("phone")}
                    />
                </Col>

                <Col md="3" className="mb-3">
                    <NeoInput
                        label="Gênero"
                        name="gender"
                        type="select"
                        flat={true}
                        {...formik.getFieldProps("gender")}
                    >
                        <option value="">Selecione</option>
                        {GENDER_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </NeoInput>
                </Col>

                <Col md="3" className="mb-3">
                    <NeoInput
                        label="Data de Nascimento"
                        placeholder="DD/MM/AAAA"
                        name="birthDate"
                        flat={true}
                        value={birthDateInput}
                        onChange={handleBirthDateChange}
                        onBlur={() => formik.setFieldTouched("birthDate", true)}
                        error={formik.touched.birthDate && formik.errors.birthDate ? formik.errors.birthDate : ""}
                    />
                </Col>
            </Row>

            <hr className="my-4 border-dashed" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }} />

            <Row className="mb-4">
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-map-marker-outline me-2 font-size-16 text-primary"></i>
                        Endereço
                    </h6>
                </Col>

                <Col md="3" className="mb-3">
                    <NeoInput
                        label="CEP"
                        name="address.zipCode"
                        type="text"
                        flat={true}
                        placeholder="00000-000"
                        {...formik.getFieldProps("address.zipCode")}
                    />
                </Col>

                <Col md="3" className="mb-3">
                    <NeoInput
                        label="Estado (UF)"
                        name="address.state"
                        type="text"
                        flat={true}
                        placeholder="Ex: SP"
                        {...formik.getFieldProps("address.state")}
                    />
                </Col>

                <Col md="6" className="mb-3">
                    <NeoInput
                        label="Cidade"
                        name="address.city"
                        type="text"
                        flat={true}
                        placeholder="Sua Cidade"
                        {...formik.getFieldProps("address.city")}
                    />
                </Col>

                <Col md="4" className="mb-3">
                    <NeoInput
                        label="Bairro"
                        name="address.neighborhood"
                        type="text"
                        flat={true}
                        placeholder="Seu Bairro"
                        {...formik.getFieldProps("address.neighborhood")}
                    />
                </Col>

                <Col md="6" className="mb-3">
                    <NeoInput
                        label="Logradouro / Endereço"
                        name="address.street"
                        type="text"
                        flat={true}
                        placeholder="Rua, Avenida..."
                        {...formik.getFieldProps("address.street")}
                    />
                </Col>

                <Col md="2" className="mb-3">
                    <NeoInput
                        label="Número"
                        name="address.number"
                        type="text"
                        flat={true}
                        placeholder="Nº / S/N"
                        {...formik.getFieldProps("address.number")}
                    />
                </Col>
            </Row>

            <hr className="my-4 border-dashed" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }} />

            <Row className="mb-4">
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-card-account-mail-outline me-2 font-size-16 text-primary"></i>
                        Informações Profissionais
                    </h6>
                </Col>

                <Col md="4" className="mb-3">
                    <NeoInput
                        label="Conselho de Classe (CREF, CRM...)"
                        name="professionalRegistration"
                        type="text"
                        flat={true}
                        placeholder="Ex: 000000-G/SP"
                        {...formik.getFieldProps("professionalRegistration")}
                    />
                </Col>
            </Row>
        </NeoCard>
    );
};

EmployeeEditor.propTypes = {
    employee: PropTypes.object,
    roles: PropTypes.array,
    onSave: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default EmployeeEditor;
